import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Form, Modal, DatePicker, Input, InputNumber, Space } from 'antd';
import { NotificationInstance } from 'antd/lib/notification/interface';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { IBill } from '@entities/bill';

import { Currency, enumLabel as currencyLabel } from '@enums/currency';
import { BillStatus } from '@enums/bill-status';

dayjs.extend(utc);

interface IBillModal extends IBaseComponentProps {
    userId: string;
    userLogin: string;
    billId: string | undefined;
    onSave: (value: IBill) => void;
    onCancel: () => void;
    api: NotificationInstance;
    currency: Currency;
}

const BillModal = (props: IBillModal) => {
    const { userLogin, userId, billId, currency, api, onSave, onCancel, serverFetch } = props;

    const { TextArea } = Input;

    const [form] = Form.useForm();
    const d = useAppDispatch();

    const [bill, setBill] = useState<IBill>();

    useEffect(() => {
        form.setFieldsValue({
            id: billId,
            userId: userId,
            status: BillStatus.Completed,
        });

        if (billId) {
            serverFetch(`bills/${billId}`, { method: 'GET' })
                .then((data: IBill) => {
                    setBill(data);

                    form.setFieldsValue({
                        billOn: data.billOn && dayjs(data.billOn).utc(true).startOf('day'),
                        comment: data.comment,
                        totalPrice: data.totalPrice,
                        status: data.status,
                    });
                })
                .catch((ex: any) => {
                    exception(api, 'Ошибка получения счета', ex, () => d(userLoaded(undefined)));
                });
        }
    }, []);

    const onSaveBill = (entity: IBill) => {
        entity.billOn = dayjs(entity.billOn).utc(true).startOf('day');
        onSave(entity);
    };

    return (
        <Modal
            destroyOnHidden={true}
            width={650}
            title={billId ? `Изменить счет "${bill?.fullNumber}"` : `Выставить новый счет для "${userLogin}"`}
            open={true}
            okText='ОК'
            onOk={() => {
                form.submit();
            }}
            onCancel={() => onCancel()}
        >
            <Form
                colon={false}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                style={{ marginTop: 20 }}
                form={form}
                onFinish={onSaveBill}
            >
                <Form.Item hidden name='id'>
                    <Input />
                </Form.Item>
                <Form.Item hidden name='userId'>
                    <Input />
                </Form.Item>
                <Form.Item hidden name='status'>
                    <Input />
                </Form.Item>
                <Form.Item label='Нименование' name='comment' rules={[{ required: true, message: 'Укажите нименование' }]}>
                    <TextArea rows={4} />
                </Form.Item>
                <Form.Item label='Дата счета' name='billOn' rules={[{ required: true, message: 'Укажите дату счета' }]}>
                    <DatePicker format={'DD.MM.YYYY'} />
                </Form.Item>
                <Form.Item label='Сумма' wrapperCol={{ span: 9 }}>
                    <Space.Compact>
                        <Form.Item noStyle name='totalPrice' rules={[{ required: true, message: 'Укажите сумму' }]}>
                            <InputNumber precision={2} decimalSeparator=',' min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Space.Addon>{currencyLabel(currency)}</Space.Addon>
                    </Space.Compact>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BillModal;
